import {USER_KEYS} from "@/api/userApi";
import {client} from "@/httpClient";
import {i18n} from "@/i18n";
import {useCategoryStore} from "@/store/category";
import {LANGUAGE_OPTIONS} from "@/store/constants";
import {create, type TSetter} from "@/store/createStore";
import useTokenStore from "@/store/token";
import type {TCampaignCreationType, TCampaignType, TNonFunctionKeys} from "@/store/types";
import qs from "qs";
import {toast} from "react-toastify";
import {z} from "zod";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

const getOnboardingSearchParams = () =>
  qs.parse(location.search, {ignoreQueryPrefix: true}) as Record<string, string | undefined>;

const FormSchema = z.object({
  email: z.email("invalidEmail"),
  password: z.string().min(8, "passwordAtLeast8Charaters"),
});

type THint = {
  completed: boolean;
  description_short_de: string;
  description_short_en: string;
  key: string;
  name_de: string;
  name_en: string;
};

export type TUploadedFile = {
  file: File;
  id: string;
  preview?: string;
  type: "image" | "pdf" | "video";
};

type TState = {
  addUploadedFiles: (files: File[]) => void;
  removeUploadedFile: (id: string) => void;
  aiCampaignCreationForm: {
    campaignType?: TCampaignCreationType;
    hints: THint[];
    isCreatingCampaign: boolean;
    isLoadingHints: boolean;
    mainPrompt: string;
    selectedKeywords: string[];
    uploadedFiles: TUploadedFile[];
  };
  crawlingFailed: boolean;
  errors: Partial<{[K in keyof TState["formState"]]: string}> & {
    overall: string | null;
  };
  formState: {
    bio: string | null;
    companyLogo: File | string | null;
    companyName: string | null;
    companyOverview: string | null;
    customerPainPoints: string | null;
    email: string;
    interests: number[];
    password: string;
    productsOverview: string | null;
  };
  getCompletionPercentageOfPromptState: () => number;
  handleHintUpdate: ({isCompleted, key}: {isCompleted: boolean; key: string}) => void;
  progressPercentage: () => number;
  sessionId: string | null;
  register: () => Promise<void>;
  resetForm: () => void;
  selectInterest: (interest: number) => void;
  resetHintsIfPromptEmptyState: () => void;
  resetAiCampaignCreationForm: () => void;
  setter: TSetter<TState>;
  validateForm: ({verifyEmailExists}: {verifyEmailExists: boolean}) => Promise<boolean>;
};

const MAX_UPLOADED_FILES = 5;
const MAX_FILE_SIZE_IMAGE_PDF = 10 * 1024 * 1024; // 10MB for images/PDFs
const MAX_FILE_SIZE_VIDEO = 100 * 1024 * 1024; // 100MB for videos

function getFileType(file: File): TUploadedFile["type"] {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  return "pdf";
}

function getMaxFileSize(file: File): number {
  return file.type.startsWith("video/") ? MAX_FILE_SIZE_VIDEO : MAX_FILE_SIZE_IMAGE_PDF;
}

const initialState = {
  aiCampaignCreationForm: {
    campaignType: "instagram" as TCampaignType,
    mainPrompt: "",
    hints: [],
    isLoadingHints: false,
    isCreatingCampaign: false,
    selectedKeywords: [] as string[],
    uploadedFiles: [] as TUploadedFile[],
  },
  crawlingFailed: false,
  errors: {
    email: "",
    password: "",
    overall: "",
  },
  formState: {
    bio: null,
    companyLogo: null,
    companyName: null,
    companyOverview: null,
    customerPainPoints: null,
    email: "",
    interests: [],
    password: "",
    productsOverview: null,
  },
  sessionId: null,
} satisfies Pick<TState, TNonFunctionKeys<TState>>;

const useOnboardingStore = create<TState>()(
  immer(
    devtools(
      (set, get) => ({
        ...initialState,
        addUploadedFiles: (files: File[]) => {
          const tooLargeNames: string[] = [];

          set(
            state => {
              const current = state.aiCampaignCreationForm.uploadedFiles;
              const remaining = MAX_UPLOADED_FILES - current.length;
              if (remaining <= 0) return;

              const validFiles: File[] = [];
              for (const f of files) {
                if (f.size > getMaxFileSize(f)) {
                  tooLargeNames.push(f.name);
                } else {
                  validFiles.push(f);
                }
              }

              for (const file of validFiles.slice(0, remaining)) {
                const uploaded: TUploadedFile = {
                  id: crypto.randomUUID(),
                  file,
                  type: getFileType(file),
                  preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : undefined,
                };
                state.aiCampaignCreationForm.uploadedFiles.push(uploaded);
              }
            },
            false,
            "useOnboardingStore/addUploadedFiles",
          );

          if (tooLargeNames.length > 0) {
            toast.warning(`${tooLargeNames.join(", ")}: ${i18n.t("ai_campaign_creation:file_too_large")}`);
          }
        },
        removeUploadedFile: (id: string) => {
          set(
            state => {
              const idx = state.aiCampaignCreationForm.uploadedFiles.findIndex(f => f.id === id);
              if (idx !== -1) {
                const file = state.aiCampaignCreationForm.uploadedFiles[idx];
                if (file.preview) URL.revokeObjectURL(file.preview);
                state.aiCampaignCreationForm.uploadedFiles.splice(idx, 1);
              }
            },
            false,
            "useOnboardingStore/removeUploadedFile",
          );
          get().resetHintsIfPromptEmptyState();
        },
        getCompletionPercentageOfPromptState: () => {
          const hints = get().aiCampaignCreationForm.hints;
          const prompt = get().aiCampaignCreationForm.mainPrompt;

          if (hints.length === 0) return prompt.trim().length > 0 ? 50 : 0;

          const completedHints = hints.filter(h => h.completed).length;
          return Math.round((completedHints / hints.length) * 100);
        },
        handleHintUpdate: ({isCompleted, key}) => {
          set(state => {
            const hint = state.aiCampaignCreationForm.hints.find(h => h.key === key);
            if (hint) {
              hint.completed = isCompleted;
            }
          });
        },
        progressPercentage: () => {
          const formState = get().formState;

          if (formState.customerPainPoints !== null || get().crawlingFailed) return 100;
          if (formState.productsOverview !== null) return 75;
          if (formState.companyOverview !== null) return 50;
          if (formState.companyName !== null) return 25;

          return 0;
        },
        register: async () => {
          const search = getOnboardingSearchParams();
          const tokenState = useTokenStore.getState();
          // Who are we writing to?
          //  - logged in AND the URL carries user_id (arrived via the finish-setup modal's
          //    AI CTA): update that account's company profile only, never name/password.
          //  - URL carries access_token + user_id but not logged in: a pre-created account
          //    finishing setup (e.g. after a payment link) - update it, still setting
          //    name/password.
          //  - otherwise (incl. a stray session with no finish context): brand-new signup.
          const hasUrlExistingUser = !!search.access_token && !!search.user_id;
          const isLoggedInFinish = !!tokenState.userId && !!search.user_id;

          const formData = new FormData();
          formData.append("bio", get().formState.bio ?? "");
          formData.append("company_overview", get().formState.companyOverview!);
          formData.append("company_name", get().formState.companyName!);
          formData.append("customer_pain_points", get().formState.customerPainPoints!);
          for (const interest of get().formState.interests) {
            formData.append("interests", String(interest));
          }
          formData.append(
            "language_code",
            LANGUAGE_OPTIONS.find(option => option.i18nextCode === i18n.resolvedLanguage)?.code ?? "de_CH",
          );
          formData.append("products_overview", get().formState.productsOverview!);
          formData.append("type", "company");

          if (!!get().formState.companyLogo && typeof get().formState.companyLogo !== "string") {
            formData.append("picture", get().formState.companyLogo!);
            formData.append("picture320", get().formState.companyLogo!);
            formData.append("picture640", get().formState.companyLogo!);
          }

          if (search.country) {
            formData.append("country", search.country);
          }

          if (search.domain) {
            formData.append("website", search.domain as string);
          }

          if (get().sessionId) {
            formData.append("session_id", get().sessionId!);
          }

          // Name/password are only sent by a new signup or a payment-link finisher (who type
          // them in). A logged-in user is only updating their profile - sending an empty
          // password would blank the account's password server-side, so never send it.
          if (!isLoggedInFinish) {
            formData.append("first_name", get().formState.email.split("@")[0]);
            if (get().formState.password) {
              formData.append("password", get().formState.password);
            }
          }

          // A logged-in finish updates its own account via the session token, even if the URL
          // also carries token params (guards against blanking the password via empty fields).
          if (isLoggedInFinish) {
            return client.patch(USER_KEYS.mUser(Number(tokenState.userId))[0], formData, {
              headers: {"Content-Type": "multipart/form-data"},
            });
          }

          if (hasUrlExistingUser) {
            return client.patch(USER_KEYS.mUser(Number(search.user_id))[0], formData, {
              headers: {
                "Content-Type": "multipart/form-data",
                Authorization: `Bearer ${search.access_token}`,
              },
            });
          }

          formData.append("email", get().formState.email);
          return client.post(USER_KEYS.mUserList[0], formData, {
            headers: {"Content-Type": "multipart/form-data"},
          });
        },
        resetForm: () =>
          set(
            state => {
              state.formState = initialState.formState;
              state.sessionId = null;
            },
            false,
            "useOnboardingStore/resetForm",
          ),
        selectInterest: interest => {
          const category = useCategoryStore.getState().categories.find(category => category.id === interest);

          if (!!category) {
            set(
              state => {
                if (state.formState.interests.includes(category.id)) {
                  state.formState.interests = state.formState.interests.filter(i => i !== category.id);
                } else {
                  state.formState.interests.push(category.id);
                }
              },
              false,
              "useOnboardingStore/selectInterest",
            );
          }
        },
        resetHintsIfPromptEmptyState: () => {
          const prompt = get().aiCampaignCreationForm.mainPrompt;
          if (prompt.trim().length === 0) {
            set(
              state => {
                state.aiCampaignCreationForm.hints = state.aiCampaignCreationForm.hints.map(hint => ({
                  ...hint,
                  completed: false,
                }));
              },
              false,
              "useOnboardingStore/resetHintsIfPromptEmptyState",
            );
          }
        },
        resetAiCampaignCreationForm: () => {
          // Revoke preview URLs before clearing
          const files = get().aiCampaignCreationForm.uploadedFiles;
          for (const f of files) {
            if (f.preview) URL.revokeObjectURL(f.preview);
          }
          set(
            state => {
              state.aiCampaignCreationForm = {...initialState.aiCampaignCreationForm, uploadedFiles: []};
            },
            false,
            "useOnboardingStore/resetAiCampaignCreationForm",
          );
        },
        setter: fn => set(fn, false, "useOnboardingStore/setter"),
        validateForm: async ({verifyEmailExists}) => {
          const result = FormSchema.safeParse(get().formState);

          if (!result.success) {
            Object.entries(result.error.format()).forEach(([key, value]) => {
              if (value && "_errors" in value) {
                get().setter(state => {
                  state.errors[key as keyof TState["formState"]] = value._errors[0];
                });
              }
            });

            return false;
          }

          if (verifyEmailExists) {
            const response = await client.get<{exists: boolean}>(USER_KEYS.mUserEmail(get().formState.email)[0]);

            if (response.data.exists) {
              get().setter(state => {
                state.errors.email = "emailAlreadyExists";
              });

              return false;
            }
          }

          return true;
        },
      }),
      {name: "useOnboardingStore"},
    ),
  ),
);

export {useOnboardingStore};
